import React, { useState, useEffect, useCallback } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { fetchAddressSuggestions } from '../Services/GeoService';

/**
 * Address autocomplete component with latitude/longitude support
 * Provides suggestions as user types, but allows custom input
 */
function AddressAutocomplete({
  id,
  value,
  onChange,
  onCoordinatesChange,
  placeholder,
  className,
  disabled,
  isInvalid,
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Track the last value we sent to parent to prevent sync loops
  const lastSentValue = React.useRef(value || '');
  // Track if a suggestion was just selected (to prevent clearing on subsequent onChange)
  const justSelectedSuggestion = React.useRef(false);

  // Sync local query state when value prop changes (e.g., from parent)
  // Only sync if the change came from outside (not from our own onChange)
  useEffect(() => {
    // If the value matches what we last sent, parent has caught up - no action needed
    if (value === lastSentValue.current) {
      return;
    }
    // Value changed from outside - sync to local state
    if (value !== undefined && value !== query) {
      setQuery(value);
      lastSentValue.current = value;
    }
  }, [value, query]);

  // Debounced fetch suggestions
  const debouncedFetch = useCallback(
    _.debounce(async (searchQuery) => {
      if (searchQuery.length < 3) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await fetchAddressSuggestions(searchQuery);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (error) {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [],
  );

  // Cleanup debounce on unmount
  useEffect(() => () => {
    debouncedFetch.cancel();
  }, [debouncedFetch]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    setSelectedIndex(-1);

    // If we just selected a suggestion, don't clear coordinates or call onChange again
    // (the selection already updated everything)
    if (justSelectedSuggestion.current) {
      justSelectedSuggestion.current = false;
      return;
    }

    // Track what we're sending to prevent sync loops
    lastSentValue.current = newValue;
    onChange(newValue);
    // Clear coordinates when user types - they must select a suggestion to get new coords
    if (onCoordinatesChange) {
      onCoordinatesChange({ lat: null, lon: null });
    }
    debouncedFetch(newValue);
  };

  const handleSuggestionClick = (suggestion) => {
    // Mark that we just selected a suggestion to prevent handleInputChange from clearing
    justSelectedSuggestion.current = true;

    setQuery(suggestion.displayName);
    setSuggestions([]);
    setShowSuggestions(false);
    // Track what we're sending to prevent sync loops
    lastSentValue.current = suggestion.displayName;
    onChange(suggestion.displayName);

    // Notify parent about coordinates
    if (onCoordinatesChange) {
      onCoordinatesChange({
        lat: suggestion.lat,
        lon: suggestion.lon,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow click to register
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="address-autocomplete position-relative">
      <Form.Control
        id={id}
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        isInvalid={isInvalid}
        autoComplete="off"
      />
      {isLoading && (
        <div className="position-absolute" style={{ right: '10px', top: '8px' }}>
          <Spinner animation="border" size="sm" variant="secondary" />
        </div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <ListGroup
          className="position-absolute w-100 shadow-sm"
          style={{ zIndex: 1060, maxHeight: '200px', overflowY: 'auto' }}
        >
          {suggestions.map((suggestion, index) => (
            <ListGroup.Item
              key={`${suggestion.lat}-${suggestion.lon}`}
              action
              active={index === selectedIndex}
              onClick={() => handleSuggestionClick(suggestion)}
              className="small py-2"
              style={{ cursor: 'pointer' }}
            >
              <i className="fa fa-map-marker-alt me-2 text-muted" />
              {suggestion.displayName}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
}

AddressAutocomplete.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCoordinatesChange: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
};

AddressAutocomplete.defaultProps = {
  id: undefined,
  value: '',
  onCoordinatesChange: undefined,
  placeholder: 'Enter address...',
  className: '',
  disabled: false,
  isInvalid: false,
};

export default AddressAutocomplete;
